import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { TranslocoService } from '@ngneat/transloco';
import { Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { SelectItem, ConfirmationService } from 'primeng/api';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { CandidateModel } from 'projects/core/src/lib/models/candidate.model';
import { EmployeeModel } from 'projects/core/src/lib/models/employee.model';
import { EmployeeSearchComponent } from 'projects/admin/src/app/shared/employee-search/employee-search.component';
import { SALUTATIONS } from 'projects/core/src/lib/constants';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

enum EditMode {
  Create = 'create',
  Edit = 'edit',
}

class CustomCandidateModel extends CandidateModel {
  editMode: EditMode;
}

@Component({
  selector: 'app-ac-persons-candidates',
  templateUrl: './ac-persons-candidates.component.html',
  styleUrls: ['./ac-persons-candidates.component.scss']
})
export class AcPersonsCandidatesComponent implements OnInit, OnChanges {

  @Input() ac: AcModel = null;
  @Input() inEdit: boolean;
  @Input() submitting: boolean;

  @Output() sendInvitation = new EventEmitter<any>();

  @ViewChild('employeesearch') employeesearch: EmployeeSearchComponent;

  public candidates: Array<CustomCandidateModel> = [];
  public dialogVisible = false;
  public dialogMode: EditMode;
  public dialogCandidateId: number = null;

  public candidateForm: UntypedFormGroup = this.fb.group({
    gender: new UntypedFormControl('', [Validators.required]),
    firstname: new UntypedFormControl('', [Validators.required]),
    lastname: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, CustomValidators.email()]),
    phone: new UntypedFormControl(''),
    mobile: new UntypedFormControl(''),
  });
  public optionsSalutations: Array<SelectItem> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.setCandidates();
    this.optionsSalutations = FormHelper.getSelectList(SALUTATIONS, null, this.transloco);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ac || changes.inEdit) {
      this.setCandidates();
    }
  }

  public canAddCandidate(): boolean {
    const group = this.ac?.template?.isGroupAc() || false;
    return this.submitting !== true && (group === true || this.candidates.length < 1);
  }

  public addCandidate(): void {
    if (this.inEdit === true && this.canAddCandidate() === true && this.canEditCandidates() === true) {
      this.dialogMode = EditMode.Create;
      this.dialogVisible = true;
      this.dialogCandidateId = null;
    }
  }

  public editCandidate(candidate: CustomCandidateModel): void {
    if (this.inEdit === true && this.canEditCandidates() === true) {
      this.candidateForm.patchValue({
        gender: this.optionsSalutations.find(gender => {
          return gender.value === candidate.gender;
        })?.value,
        firstname: candidate.firstname,
        lastname: candidate.lastname,
        email: candidate.email,
        phone: candidate.phone,
        mobile: candidate.mobile,
      });
      this.dialogMode = EditMode.Edit;
      this.dialogVisible = true;
      this.dialogCandidateId = candidate.id;
    }
  }

  public cancelCandidate(): void {
    if (this.inEdit === true) {
      this.dialogVisible = false;
      this.clearForm();
    }
  }

  public saveCandidate(): void {
    if (this.inEdit === true) {
      if (this.candidateForm.valid === true) {
        let changeModeToCreate = false;
        const candidate = this.candidates.find(actualCandidate => actualCandidate.id === this.dialogCandidateId);
        if (this.dialogMode === EditMode.Edit && this.candidateForm.get('email').value !== candidate?.email) {
          changeModeToCreate = true;
        }
        const data = {
          gender: this.candidateForm.get('gender').value,
          firstname: this.candidateForm.get('firstname').value,
          lastname: this.candidateForm.get('lastname').value,
          email: this.candidateForm.get('email').value,
          phone: this.candidateForm.get('phone').value,
          mobile: this.candidateForm.get('mobile').value,
        };
        if (this.dialogMode === EditMode.Create) {
          this.candidates.push(
            new CustomCandidateModel().deserialize({...data, editMode: EditMode.Create})
          );
        } else {
          if (candidate !== undefined) {
            if (changeModeToCreate === true) {
              candidate.deserialize({...data, editMode: EditMode.Create});
            } else {
              candidate.deserialize(data);
            }
          }
        }
        this.dialogVisible = false;
        this.clearForm();
      } else {
        FormHelper.markFormAsDirty(this.candidateForm);
      }
    }
  }

  public deleteCandidate(candidate: CustomCandidateModel): void {
    if (this.inEdit === true && this.candidates && this.canEditCandidates() === true) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: candidate.fullname }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.candidates = this.candidates.filter((actualCandidate: CustomCandidateModel) => {
            return actualCandidate.id !== candidate.id;
          });
        }
      });
    }
  }

  public onResendInvitation(candidate: CustomCandidateModel): void {
    this.sendInvitation.emit({
      role: 'candidate',
      id: candidate.hashed_id,
      name: candidate.fullname
    });
  }


  public onOpenEmployeeSearch(): void {
    this.employeesearch.open();
  }

  public onSelectEmployee(employee: EmployeeModel): void {
    this.candidateForm.patchValue({
      gender: this.optionsSalutations.find(gender => gender.value[0] === employee.gender)?.value,
      firstname: employee.firstname,
      lastname: employee.lastname,
      email: employee.email,
      phone: employee.phone,
      mobile: employee.mobile,
    });
  }

  public getPayload(): Array<any> {
    return this.candidates.map((candidate: CustomCandidateModel) => {
      return {
        id: candidate.id,
        hashed_id: candidate.hashed_id,
        gender: candidate.gender,
        firstname: candidate.firstname,
        lastname: candidate.lastname,
        email: candidate.email,
        phone: candidate.phone,
        mobile: candidate.mobile,
        mode: candidate.editMode
      };
    });
  }

  public canResendEmail(candidate: CustomCandidateModel): boolean {
    return candidate.invited_at !== null && this.hasStatus(['planned', 'running']);
  }

  public canEditCandidates(): boolean {
    return this.hasStatus(['requested', 'planned']);
  }

  private hasStatus(list: Array<string>): boolean {
    return list.find(status => status === this.ac?.status) !== undefined;
  }

  private setCandidates(): void {
    this.candidates = this.ac?.candidates.map((candidate: CandidateModel) => {
      return new CustomCandidateModel().deserialize({...candidate, editMode: EditMode.Edit});
    }) || [];
  }

  private clearForm(): void {
    this.candidateForm.patchValue({
      gender: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      mobile: '',
    });
  }

}
